import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import OrgSelector from "../components/OrgSelector";
import { API } from "../App";
import axios from "axios";
import {
  ShieldCheck, Plus, CheckCircle2, Clock, Circle, MinusCircle,
  Loader2, ChevronDown, ChevronRight, Target, AlertTriangle,
  CalendarClock, Users, FileText
} from "lucide-react";

const STATUS_ICONS = {
  pending: { icon: Circle, color: "#8B95A5" },
  "in-progress": { icon: Clock, color: "#38BDF8" },
  completed: { icon: CheckCircle2, color: "#4ADE80" },
  "not-applicable": { icon: MinusCircle, color: "#6B7280" },
};

const AUDIT_TYPES = [
  { value: "pre-launch", label: "Pre-Launch Audit" },
  { value: "recertification", label: "Recertification" },
  { value: "surveillance", label: "Surveillance Audit" },
  { value: "internal", label: "Internal Audit" },
  { value: "supplier", label: "Supplier Audit" },
  { value: "customer", label: "Customer Audit" },
];

const AuditReadinessPage = () => {
  const { t } = useTranslation('audit');
  const [standards, setStandards] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [gapAssessments, setGapAssessments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [form, setForm] = useState({
    title: "", audit_type: "", standard: "", client_name: "",
    sector: "", target_date: "", gap_assessment_id: ""
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [stdRes, readRes, gapRes] = await Promise.all([
        axios.get(`${API}/standards`, { withCredentials: true }),
        axios.get(`${API}/audit-readiness`, { withCredentials: true }),
        axios.get(`${API}/gap-assessments`, { withCredentials: true }),
      ]);
      setStandards(stdRes.data);
      setAssessments(readRes.data);
      setGapAssessments(gapRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.title || !form.audit_type || !form.standard || !form.client_name || !form.sector) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/audit-readiness/create`, form, { withCredentials: true });
      setSelected(res.data);
      setShowCreate(false);
      loadAll();
      setForm({ title: "", audit_type: "", standard: "", client_name: "", org_id: "", org_name: "", vendor_id: "", sector: "", target_date: "", gap_assessment_id: "" });
    } catch (e) {
      console.error(e);
      alert("Failed to create readiness assessment");
    }
    setGenerating(false);
  };

  const updateChecklistItem = async (itemId, newStatus) => {
    if (!selected) return;
    try {
      const res = await axios.put(
        `${API}/audit-readiness/${selected.readiness_id}/checklist/${itemId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      const updatedItems = selected.checklist_items.map(i =>
        i.item_id === itemId ? { ...i, status: newStatus } : i
      );
      setSelected({ ...selected, checklist_items: updatedItems, readiness_score: res.data.readiness_score });
      setAssessments(prev => prev.map(a =>
        a.readiness_id === selected.readiness_id
          ? { ...a, checklist_items: updatedItems, readiness_score: res.data.readiness_score }
          : a
      ));
    } catch (e) { console.error(e); }
  };

  const togglePhase = (idx) => {
    setExpandedPhases(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const sectors = ["life-sciences", "automotive", "aerospace", "industrial", "iot", "medical-devices"];

  const getPhaseItems = (phase) => {
    if (!selected?.checklist_items || !phase?.items) return [];
    return selected.checklist_items.filter(ci => phase.items.includes(ci.item_id));
  };

  const getReadinessColor = (score) => {
    if (score >= 80) return "#4ADE80";
    if (score >= 50) return "#FBBF24";
    return "#F87171";
  };

  return (
    <QMSLayout title={t('readiness.title')}>
      <div className="space-y-6" data-testid="audit-readiness-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-[#8B95A5] text-sm">
            {t('readiness.subtitle')}
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#E8A838] hover:bg-[#d4962f] text-[#111318] text-sm font-medium rounded-[3px] transition-colors"
            data-testid="new-readiness-btn"
          >
            <Plus size={16} />
            {t('readiness.newAssessment')}
          </button>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-readiness-modal">
              <div className="p-6 border-b border-[rgba(255,255,255,0.07)]">
                <h2 className="text-lg font-['Syne'] font-bold text-white">New Audit Readiness Assessment</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('readiness.formTitle')}</label>
                  <input
                    value={form.title}
                    onChange={e => setForm({...form, title: e.target.value})}
                    placeholder="e.g., ISO 9001 Surveillance Audit Prep"
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                    data-testid="readiness-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('readiness.auditType')}</label>
                    <select
                      value={form.audit_type}
                      onChange={e => setForm({...form, audit_type: e.target.value})}
                      className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                      data-testid="audit-type-select"
                    >
                      <option value="">{t('readiness.selectType')}</option>
                      {AUDIT_TYPES.map(atype => (
                        <option key={atype.value} value={atype.value}>{atype.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('readiness.standard')}</label>
                    <select
                      value={form.standard}
                      onChange={e => setForm({...form, standard: e.target.value})}
                      className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                      data-testid="readiness-standard-select"
                    >
                      <option value="">{t('readiness.selectStandard')}</option>
                      {standards.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('readiness.clientName')}</label>
                    <OrgSelector
                      value={form.org_id}
                      onChange={(orgId, orgName) => setForm({...form, org_id: orgId, org_name: orgName, client_name: orgName || form.client_name})}
                      showVendorId={true}
                      vendorId={form.vendor_id}
                      onVendorIdChange={v => setForm({...form, vendor_id: v})}
                    />
                    {!form.org_id && (
                      <input
                        value={form.client_name}
                        onChange={e => setForm({...form, client_name: e.target.value})}
                        placeholder="Client name"
                        className="w-full mt-2 px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                        data-testid="readiness-client-input"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('readiness.sector')}</label>
                    <select
                      value={form.sector}
                      onChange={e => setForm({...form, sector: e.target.value})}
                      className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                    >
                      <option value="">{t('readiness.selectSector')}</option>
                      {sectors.map(s => (
                        <option key={s} value={s}>{s.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">Target Date</label>
                    <input
                      type="date"
                      value={form.target_date}
                      onChange={e => setForm({...form, target_date: e.target.value})}
                      className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('readiness.linkGapAssessment')}</label>
                    <select
                      value={form.gap_assessment_id}
                      onChange={e => setForm({...form, gap_assessment_id: e.target.value})}
                      className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                    >
                      <option value="">{t('readiness.none')}</option>
                      {gapAssessments.map(g => (
                        <option key={g.assessment_id} value={g.assessment_id}>{g.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-[#8B95A5] hover:text-white text-sm">{t('common:actions.cancel')}</button>
                <button
                  onClick={handleCreate}
                  disabled={generating || !form.title || !form.audit_type || !form.standard || !form.client_name || !form.sector}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#E8A838] hover:bg-[#d4962f] disabled:opacity-50 text-[#111318] text-sm font-medium rounded-[3px] transition-colors"
                  data-testid="generate-readiness-btn"
                >
                  {generating ? <><Loader2 size={16} className="animate-spin" /> {t('readiness.generating')}</> : <><ShieldCheck size={16} /> {t('readiness.generateChecklist')}</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="text-sm font-medium text-[#8B95A5] uppercase tracking-wider">{t('readiness.assessments')}</h3>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
            ) : assessments.length === 0 ? (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-8 text-center">
                <ShieldCheck className="mx-auto mb-3 text-[#8B95A5]" size={32} />
                <p className="text-[#8B95A5] text-sm">{t('readiness.noAssessments')}</p>
              </div>
            ) : (
              assessments.map(a => (
                <div
                  key={a.readiness_id}
                  onClick={() => { setSelected(a); setExpandedPhases({}); }}
                  className={`bg-[#1F222D] border rounded-[3px] p-4 cursor-pointer transition-all hover:border-[#E8A838]/40 ${
                    selected?.readiness_id === a.readiness_id ? "border-[#E8A838]" : "border-[rgba(255,255,255,0.07)]"
                  }`}
                  data-testid={`readiness-item-${a.readiness_id}`}
                >
                  <h4 className="text-white text-sm font-medium truncate">{a.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[#38BDF8] text-xs">{a.standard_name}</span>
                    <span className="text-[#8B95A5] text-xs">|</span>
                    <span className="text-[#8B95A5] text-xs capitalize">{a.audit_type?.replace(/-/g, ' ')}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[#8B95A5] text-xs">{a.client_name}</span>
                    <div className="flex items-center gap-1">
                      <div className="w-16 h-1.5 bg-[#111318] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${a.readiness_score || 0}%`, background: getReadinessColor(a.readiness_score || 0) }} />
                      </div>
                      <span className="text-xs" style={{ color: getReadinessColor(a.readiness_score || 0) }}>
                        {a.readiness_score || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2">
            {selected ? (
              <div className="space-y-4">
                {/* Header Card */}
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-['Syne'] font-bold">{selected.title}</h3>
                      <p className="text-[#38BDF8] text-sm mt-1">{selected.standard_name} | {selected.audit_type?.replace(/-/g, ' ')}</p>
                      <div className="flex items-center gap-4 mt-2 text-[#8B95A5] text-xs">
                        <span className="flex items-center gap-1"><Users size={12} /> {selected.client_name}</span>
                        {selected.target_date && <span className="flex items-center gap-1"><CalendarClock size={12} /> {t('readiness.target')}: {selected.target_date}</span>}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                          <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#2D313D" strokeWidth="3" />
                          <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={getReadinessColor(selected.readiness_score || 0)} strokeWidth="3" strokeDasharray={`${selected.readiness_score || 0}, 100`} />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold" style={{ color: getReadinessColor(selected.readiness_score || 0) }}>
                            {Math.round(selected.readiness_score || 0)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Areas */}
                {selected.risk_areas?.length > 0 && (
                  <div className="bg-[rgba(251,191,36,0.05)] border border-[rgba(251,191,36,0.15)] rounded-[3px] p-4">
                    <h4 className="text-[#FBBF24] text-sm font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle size={14} /> {t('readiness.keyRiskAreas')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selected.risk_areas.map((r, i) => (
                        <span key={i} className="text-[#CBD5E1] text-xs bg-[rgba(251,191,36,0.08)] px-2 py-1 rounded">{r}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Phased Checklist */}
                {selected.phases?.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="text-[#8B95A5] text-xs uppercase tracking-wider font-medium">{t('readiness.preparationPhases')}</h4>
                    {selected.phases.map((phase, idx) => {
                      const phaseItems = getPhaseItems(phase);
                      const completedCount = phaseItems.filter(i => i.status === "completed").length;
                      return (
                        <div key={idx} className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] overflow-hidden">
                          <div
                            className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[rgba(255,255,255,0.02)]"
                            onClick={() => togglePhase(idx)}
                          >
                            {expandedPhases[idx] ? <ChevronDown size={14} className="text-[#8B95A5]" /> : <ChevronRight size={14} className="text-[#8B95A5]" />}
                            <div className="flex-1">
                              <span className="text-white text-sm font-medium">{phase.name}</span>
                              <span className="text-[#8B95A5] text-xs ml-2">({phase.duration})</span>
                            </div>
                            <span className="text-xs text-[#8B95A5]">{completedCount}/{phaseItems.length}</span>
                          </div>
                          {expandedPhases[idx] && phaseItems.length > 0 && (
                            <div className="border-t border-[rgba(255,255,255,0.05)]">
                              {phaseItems.map(item => {
                                const st = STATUS_ICONS[item.status] || STATUS_ICONS.pending;
                                const Icon = st.icon;
                                return (
                                  <div key={item.item_id} className="flex items-center gap-3 px-4 py-3 hover:bg-[rgba(255,255,255,0.02)] border-b border-[rgba(255,255,255,0.03)] last:border-0">
                                    <button
                                      onClick={() => {
                                        const next = item.status === "completed" ? "pending" : item.status === "pending" ? "in-progress" : "completed";
                                        updateChecklistItem(item.item_id, next);
                                      }}
                                      className="shrink-0"
                                      data-testid={`checklist-${item.item_id}`}
                                    >
                                      <Icon size={16} style={{ color: st.color }} />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm ${item.status === "completed" ? "text-[#8B95A5] line-through" : "text-[#CBD5E1]"}`}>
                                        {item.description}
                                      </p>
                                      {item.priority && (
                                        <span className="text-xs capitalize" style={{
                                          color: item.priority === "high" ? "#F87171" : item.priority === "medium" ? "#FBBF24" : "#8B95A5"
                                        }}>
                                          {item.priority} {t('readiness.priority')}
                                        </span>
                                      )}
                                    </div>
                                    <select
                                      value={item.status}
                                      onChange={e => updateChecklistItem(item.item_id, e.target.value)}
                                      className="text-xs bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-[#CBD5E1] outline-none"
                                    >
                                      <option value="pending">{t('readiness.statusPending')}</option>
                                      <option value="in-progress">{t('readiness.statusInProgress')}</option>
                                      <option value="completed">{t('readiness.statusCompleted')}</option>
                                      <option value="not-applicable">{t('readiness.statusNA')}</option>
                                    </select>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* All items flat if no phases */
                  <div className="space-y-2">
                    <h4 className="text-[#8B95A5] text-xs uppercase tracking-wider font-medium">{t('readiness.checklistItems')}</h4>
                    {selected.checklist_items?.map(item => {
                      const st = STATUS_ICONS[item.status] || STATUS_ICONS.pending;
                      const Icon = st.icon;
                      return (
                        <div key={item.item_id} className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] flex items-center gap-3 px-4 py-3">
                          <button onClick={() => {
                            const next = item.status === "completed" ? "pending" : "completed";
                            updateChecklistItem(item.item_id, next);
                          }}>
                            <Icon size={16} style={{ color: st.color }} />
                          </button>
                          <div className="flex-1">
                            <p className={`text-sm ${item.status === "completed" ? "text-[#8B95A5] line-through" : "text-[#CBD5E1]"}`}>
                              {item.description}
                            </p>
                          </div>
                          <select
                            value={item.status}
                            onChange={e => updateChecklistItem(item.item_id, e.target.value)}
                            className="text-xs bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-[#CBD5E1] outline-none"
                          >
                            <option value="pending">{t('readiness.statusPending')}</option>
                            <option value="in-progress">{t('readiness.statusInProgress')}</option>
                            <option value="completed">{t('readiness.statusCompleted')}</option>
                            <option value="not-applicable">{t('readiness.statusNA')}</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Recommendations */}
                {selected.recommendations && (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                    <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-2">
                      <FileText size={14} className="text-[#E8A838]" /> {t('readiness.aiRecommendations')}
                    </h4>
                    <p className="text-[#CBD5E1] text-sm leading-relaxed">{selected.recommendations}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-12 text-center">
                <Target className="mx-auto mb-4 text-[#8B95A5]" size={40} />
                <h3 className="text-white text-lg font-['Syne'] font-bold mb-2">{t('readiness.title')}</h3>
                <p className="text-[#8B95A5] text-sm">{t('readiness.selectOrCreate')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default AuditReadinessPage;
